/**
 * CAOS-1586 — where a record lives, decided in one place.
 *
 * Every link to a record used to be assembled where it was needed: `/{appKey}/{tabKey}/{id}`
 * written out as a literal in eleven places in `content-source.ts` — reference chips, related-list
 * rows, breadcrumbs, search hits, the list-view chooser, and the navigate() after a save. The shape
 * was not the problem; the shape being a LITERAL was. Two things follow from it, and this module
 * exists to make both answerable.
 *
 * ── The shape has no single owner ───────────────────────────────────────────
 * Eleven copies of a route is eleven places to change when the route changes, and eleven chances
 * for one of them to be missed — and the one that is missed does not fail, it produces a link that
 * quietly goes somewhere wrong. Composing the address in one function makes that a compile-time
 * question rather than a grep.
 *
 * ── Which tab a record belongs to is not authored anywhere ──────────────────
 * The harder half, and the reason this is a ticket rather than a tidy-up. The host decides the tab
 * by walking the CURRENT app's tabs and taking whichever one names the object. That is not a
 * lookup, it is a guess, and it is underdetermined twice over:
 *
 *   - WITHIN an app: `AppSchema.tabs` is a plain array of refs with no uniqueness constraint, and
 *     the kernel has no deploy validation forbidding two tabs on one object. When there are two,
 *     the map keeps whichever came LAST, so the address depends on the order tabs were authored in.
 *   - ACROSS apps: the host is handed only the current app's tabs, so the same record reached from
 *     two apps gets two addresses, and a package-contributed surface cannot link to a record at all
 *     except by rebuilding this shape itself.
 *
 * So a tab may DECLARE that records of its object live there, and that declaration travels with the
 * metadata rather than being re-derived per app. {@link recordDestination} prefers it when present.
 *
 * ── What this deliberately does not do ──────────────────────────────────────
 * It does not require an object to have one home. A record can legitimately be reached from several
 * apps, and forcing a single address would be a worse answer than the guess it replaces. An object
 * that declares nothing keeps exactly the behaviour it has today, ORDER INCLUDED: the fallback
 * still takes the last matching tab, because changing an arbitrary rule to a different arbitrary
 * rule would move existing links for orgs that author nothing, and buy nothing for the orgs that do.
 * Authoring is the fix for the ambiguity; a different tie-break is not.
 *
 * It also composes URLs and nothing else. The kernel names a DESTINATION — an app and a tab — and
 * the address shape stays here, because a URL is UI and the Engine does not know about UI.
 */

import type { ShellNavTab } from './kernel-contract.js';

/** The app and tab a record or list is reached through. */
export interface Destination {
  readonly appKey: string;
  readonly tabKey: string;
}

/**
 * Percent-encode one path segment.
 *
 * On every value this is actually given, this is the IDENTITY: a component key is
 * `[a-z][a-z0-9_]*` joined by dots (`COMPONENT_KEY_PATTERN`, kernel `meta/shared.ts`) and a record
 * id is a uuid, and every character in both is URI-unreserved. It is here for the case that is not
 * supposed to happen — an id arriving from somewhere that did not check it — where the difference
 * is between a wrong address and a broken one. `caos-1586-record-address.test.ts` pins the identity
 * so this can never silently start rewriting keys that used to pass through untouched.
 */
const seg = (value: string): string => encodeURIComponent(value);

/** The address of one record: `/{app}/{tab}/{id}`. */
export function recordAddress(dest: Destination, recordId: string): string {
  return `/${seg(dest.appKey)}/${seg(dest.tabKey)}/${seg(String(recordId))}`;
}

/**
 * The address of a tab's list: `/{app}/{tab}`, or `/{app}/{tab}?view={viewKey}` for a saved view.
 *
 * A saved view decides the columns AND the conditions, so it is a different address rather than a
 * different filter on this one — which is what makes it survive a reload and be linkable.
 */
export function listAddress(dest: Destination, viewKey?: string): string {
  const base = `/${seg(dest.appKey)}/${seg(dest.tabKey)}`;
  return viewKey === undefined || viewKey === '' ? base : `${base}?view=${encodeURIComponent(viewKey)}`;
}

/** What {@link recordDestination} needs from the surrounding surface. */
export interface DestinationContext {
  readonly appKey: string;
  readonly tabs: readonly ShellNavTab[];
}

/**
 * Where records of `objectKey` live, or `undefined` if nowhere this caller can link to.
 *
 * `undefined` is a real answer and not a failure: a record in an object the current app does not
 * show, with no authored home, HAS no address here. Every caller renders that as a name without a
 * link, which is the honest rendering — a link that leads nowhere is worse than plain text.
 *
 * Order: the authored home first, because that is the whole point of authoring one; then the
 * current app, so an org that has authored nothing is unaffected; then nothing.
 */
export function recordDestination(
  objectKey: string | undefined,
  ctx: DestinationContext,
  homes?: ReadonlyMap<string, Destination>,
): Destination | undefined {
  if (objectKey === undefined || objectKey === '') return undefined;

  const authored = homes?.get(objectKey);
  if (authored !== undefined) return authored;

  // The incumbent guess, preserved exactly — including that the LAST matching tab wins. See the
  // header: an org that authors no home keeps the links it has today.
  let found: string | undefined;
  for (const tab of ctx.tabs) if (tab.object === objectKey) found = tab.key;
  return found === undefined ? undefined : { appKey: ctx.appKey, tabKey: found };
}

/**
 * The address of a record of `objectKey`, or `undefined` if it has no reachable home.
 *
 * The common case at a call site: resolve the destination and compose the address together, so
 * neither half is written out again.
 */
export function recordAddressFor(
  objectKey: string | undefined,
  recordId: string,
  ctx: DestinationContext,
  homes?: ReadonlyMap<string, Destination>,
): string | undefined {
  const dest = recordDestination(objectKey, ctx, homes);
  return dest === undefined ? undefined : recordAddress(dest, recordId);
}

/** An installed app as the shell plan carries it: its key, and the tabs it shows. */
export interface AppWithTabs {
  readonly sourceKey: string;
  /** Optional, because the shell plan's injection ref declares it so: an entry may carry no tabs. */
  readonly tabs?: readonly ShellNavTab[];
}

/**
 * The authored record homes across EVERY installed app.
 *
 * Built from the whole plan rather than from the current app, which is the point: the host is
 * otherwise handed only the app the reader is standing in, so it could not produce a cross-app
 * answer at all. A tab claiming `recordHome` is a claim about where that object's records live
 * everywhere, not about where they live when you happen to be in that app.
 *
 * At most one tab may claim an object — refused at deploy (`validation.tab_record_home`), and
 * checked against the whole org rather than one changeset. This does not re-litigate that: if two
 * ever arrive, the first wins and is stable, rather than the last, so a surface does not change
 * where it points depending on which app loaded first. A host that tried to adjudicate here would
 * be a second opinion on a question the deploy gate already answers.
 */
export function recordHomesOf(apps: readonly AppWithTabs[]): Map<string, Destination> {
  const homes = new Map<string, Destination>();
  for (const app of apps) {
    for (const tab of app.tabs ?? []) {
      if (tab.recordHome !== true) continue;
      if (tab.object === undefined || tab.object === '') continue;
      if (homes.has(tab.object)) continue;
      homes.set(tab.object, { appKey: app.sourceKey, tabKey: tab.key });
    }
  }
  return homes;
}
